import { Component, OnDestroy, OnInit } from '@angular/core';
import { PhoneService } from '@wephone-app-phone/services';
import { FormControl } from '@angular/forms';
import {
  SubscriptionManagedComponent,
  MessageService,
  DialogService,
  ToastService,
} from '@wephone-utils';
import { TestHeadsetModalComponent } from '@wephone-app-phone/modals/test-headset-modal/test-headset-modal.component';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { ManageRingtonesModal } from '@wephone-audio/fileentry/manage-ringtones/manage-ringtones';
import { UserProfileService } from '@wephone/services/user-profile.service';
import { UserService } from '@wephone/services/user.service';
import { ConfigManager } from '@wephone-core/wephone-core.module';
import { UserParam } from '@wephone-core/system';
import * as _ from 'lodash';
import { _ti } from '@wephone-translation';

@Component({
  selector: 'bottom-sheet-change-volume',
  templateUrl: './bottom-sheet-change-volume.component.html',
  styleUrls: ['./bottom-sheet-change-volume.component.scss']
})
export class BottomSheetChangeVolume extends SubscriptionManagedComponent implements OnInit, OnDestroy {
  audioInputOptions: any[] = [];
  audioOutputOptions: any[] = [];
  ringtones: any[] = [];
  ringDeviceOptions: any[] = [];
  inputVolume: number;
  outputVolume: number;
  ringVolume: number;
  askUserOnNewDevice: number;
  isJsSIP: boolean;

  audioInputName = new FormControl();
  audioOutputName = new FormControl();
  ringtoneCtrl = new FormControl();
  ringDeviceName = new FormControl();

  constructor(
    public readonly phoneService: PhoneService,
    private readonly messageService: MessageService,
    public dialogService: DialogService,
    private readonly profileService: UserProfileService,
    private readonly configService: ConfigManager,
    private readonly userService: UserService,
    private readonly toast: ToastService,
  ) {
    super();
    this.isJsSIP = phoneService.getSipDriverName() === 'jssip';

    const devices = this.phoneService.getAudioDevices();
    for (const device of devices) {
      if (device['is_output'] === 1) {
        this.audioOutputOptions.push(device);
        this.ringDeviceOptions.push(device);
      }
      if (device['is_input'] === 1) {
        this.audioInputOptions.push(device);
      }
    }
    this.reloadSettings();
    this.loadRingtones();

    this.addSubscription(
      this.messageService.subscribe(
        'audio_device.change',
        e => {
          this.audioInputName.setValue(this.phoneService.getActiveCaptureDeviceName());
          this.audioOutputName.setValue(this.phoneService.getActivePlaybackDeviceName());
          this.ringDeviceName.setValue(this.phoneService.getActiveRingDeviceName());
        }
      )
    );
  }

  private reloadSettings(): void {
    this.phoneService.loadPhoneConfig().then(phone_config => {
      this.inputVolume = +phone_config.input_volume;
      this.outputVolume = +phone_config.output_volume;
      this.ringVolume = +phone_config.ring_volume;
      this.askUserOnNewDevice = phone_config.ask_user_on_new_device === 0 ? 0 : 1;

      this.audioInputName.setValue(this.phoneService.getActiveCaptureDeviceName());
      this.audioOutputName.setValue(this.phoneService.getActivePlaybackDeviceName());
      this.ringDeviceName.setValue(this.phoneService.getActiveRingDeviceName());
    });
    this.ringtoneCtrl.setValue(this.configService.getUserParamValue(UserParam.ringtone_id, 0));
  }

  ngOnInit(): void {
  }

  async loadRingtones(): Promise<void> {
    this.ringtones = await this.profileService.getRingtones();
    if (_.isEmpty(this.ringtones)) {
      this.resetRingtone();
      return;
    }

    if (this.ringtoneCtrl.value) {
      const currentRingtone = this.ringtones.find(r => r.id === this.ringtoneCtrl.value);
      if (!currentRingtone) {
        this.resetRingtone();
      }
    }
  }

  async resetRingtone(): Promise<any> {
    const defaultRingtoneData = {
      ringtone_id: 0,
    };
    this.ringtoneCtrl.setValue(0);
    await this.userService.updateUserParameters(defaultRingtoneData);
    this.configService.setUserParams(defaultRingtoneData);
    this.phoneService.setRingToneUrl(defaultRingtoneData.ringtone_id);
  }

  checkHeadset(): any {
    return this.dialogService.openDialog2(TestHeadsetModalComponent, {}, (data: any) => {
      if (data) {
        this.reloadSettings();
      }
    });
  }

  changeAudioInput(event): void {
    this.phoneService.applyPhoneConfig({ input_device: event.value });
  }

  changeAudioOutput(event): void {
    this.phoneService.applyPhoneConfig({ output_device: event.value });
  }

  changeRingDevice(event): void {
    this.phoneService.applyPhoneConfig({ ring_device: event.value });
  }

  onChangeInputVolume(event): void {
    this.phoneService.applyPhoneConfig({ input_volume: event.value });
  }

  onChangeOutputVolume($event: any): void {
    this.phoneService.applyPhoneConfig({ output_volume: $event.value });
  }

  async onChangeRingtone(event: any): Promise<void> {
    const ringtone_id = +event.value;
    const data = {
      ringtone_id
    };
    try {
      await this.userService.updateUserParameters(data);
      this.configService.setUserParams(data);
      this.phoneService.setRingToneUrl(ringtone_id);
      this.toast.show(_ti('public.message.update_success'));
    } catch (error) {
      let msg = _ti('public.message.update_failure');
      if (error) {
        if (error.message) {
          msg = error.message;
        } else if (error.error && error.error.message) {
          msg = error.error.message;
        }
      }
      this.toast.showError(msg);
    }
  }

  onChangeRingVolume(event): void {
    this.phoneService.applyPhoneConfig({ ring_volume: event.value });
  }

  onChangeAskUserOnnewDevice(event: MatSlideToggleChange): void {
    this.askUserOnNewDevice = event.checked ? 1 : 0;
    this.phoneService.applyPhoneConfig({ ask_user_on_new_device: this.askUserOnNewDevice });
  }

  manageRingtones($event: any): void {
    const dialogRef = this.dialogService.openSideDialog(ManageRingtonesModal, {
      data: { selectExisting: true },
      size: 's',
      padding: false,
    });

    dialogRef.afterClosed().subscribe(res => {
      this.loadRingtones();
    });
  }
}
